<template>
  <!-- Modal picker invoked from the composer. Three tabs (release /
       track / artist), debounced search, click-to-insert. Lives at the
       panel level (teleported by el-dialog) so the composer doesn't
       have to host the overlay. -->
  <el-dialog
    v-model="visible"
    :title="$t('chat.share.title')"
    width="520px"
    align-center
    append-to-body
    destroy-on-close
    @open="onOpen"
  >
    <div class="space-y-3">
      <!-- Type tabs — same uppercase semantics as status pills -->
      <div class="flex gap-1 p-1 bg-white/[0.03] border border-white/[0.06] rounded-lg">
        <button
          v-for="t in tabs"
          :key="t.value"
          type="button"
          class="flex-1 py-1.5 text-xs font-semibold rounded-md transition-colors"
          :class="activeType === t.value
            ? 'bg-primary/[0.12] text-primary'
            : 'text-text-muted hover:text-white hover:bg-white/[0.04]'"
          @click="setType(t.value)"
        >
          {{ t.label }}
        </button>
      </div>

      <!-- Search input — focuses on dialog open -->
      <div class="relative">
        <el-icon
          class="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
          size="14"
        >
          <Search />
        </el-icon>
        <input
          ref="searchInput"
          v-model="q"
          type="text"
          :placeholder="searchPlaceholder"
          class="w-full pl-9 pr-3 py-2 text-sm bg-white/[0.04] border border-white/[0.10] rounded-md text-white placeholder-text-muted focus:outline-none focus:border-primary/50 transition-colors"
          @keydown.enter.prevent
          @keydown.esc.prevent="visible = false"
        />
      </div>

      <!-- Results -->
      <div class="max-h-[360px] overflow-y-auto scrollbar-thin scrollbar-track-transparent">
        <div v-if="loading" class="flex justify-center py-12">
          <div class="w-6 h-6 border-2 border-white/10 border-t-primary rounded-full animate-spin"></div>
        </div>

        <div v-else-if="!items.length" class="text-center py-12">
          <p class="text-sm text-text-muted">{{ $t('chat.share.empty_title') }}</p>
          <p class="text-[11px] text-text-dim mt-1">{{ $t('chat.share.empty_hint') }}</p>
        </div>

        <button
          v-for="item in items"
          v-else
          :key="`${item.type}-${item.id}`"
          type="button"
          class="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/[0.04] transition-colors text-left mb-1"
          @click="onPick(item)"
        >
          <div
            class="relative shrink-0 w-12 h-12 rounded-md overflow-hidden bg-[#0a0b0f] border border-white/[0.06]"
          >
            <img
              v-if="item.artworkUrl"
              :src="item.artworkUrl"
              :alt="item.title"
              class="w-full h-full object-cover"
              loading="lazy"
            />
            <div v-else class="w-full h-full flex items-center justify-center text-text-muted">
              <el-icon size="16"><Headset /></el-icon>
            </div>
          </div>
          <div class="min-w-0 flex-1">
            <p class="text-sm text-white font-semibold truncate">{{ item.title }}</p>
            <p class="text-[11px] text-text-muted truncate">
              {{ item.subtitle || (item.meta || '—') }}
            </p>
          </div>
          <span
            v-if="item.status"
            class="upper-keep shrink-0 text-[9px] font-bold tracking-widest px-1.5 py-0.5 rounded-full uppercase border"
            :class="statusPillClass(item.status)"
          >
            {{ item.status.toLowerCase() }}
          </span>
        </button>
      </div>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
import { Search, Headset } from '@element-plus/icons-vue'
import { useI18n } from 'vue-i18n'

type ShareItem = {
  id: string
  type: 'release' | 'track' | 'artist'
  title: string
  subtitle: string
  artworkUrl: string | null
  status: string | null
  meta: string | null
}

const props = defineProps<{ modelValue: boolean }>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'select': [item: ShareItem]
}>()

const { t } = useI18n()

const visible = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v)
})

const tabs = computed(() => [
  { value: 'release', label: t('chat.share.tab_releases') },
  { value: 'track', label: t('chat.share.tab_tracks') },
  { value: 'artist', label: t('chat.share.tab_artists') }
])

const activeType = ref<'release' | 'track' | 'artist'>('release')
const q = ref('')
const items = ref<ShareItem[]>([])
const loading = ref(false)
const searchInput = ref<HTMLInputElement | null>(null)

const searchPlaceholder = computed(() => {
  if (activeType.value === 'release') return t('chat.share.placeholder_releases')
  if (activeType.value === 'track') return t('chat.share.placeholder_tracks')
  return t('chat.share.placeholder_artists')
})

// Debounce search keystrokes so we don't hammer the endpoint with one
// request per typed character. 220ms feels responsive without being
// chatty under fast typing.
let debounceTimer: ReturnType<typeof setTimeout> | null = null
async function loadResults() {
  loading.value = true
  try {
    const res = await $fetch<{ items: ShareItem[] }>('/api/chat/share/search', {
      query: { type: activeType.value, q: q.value, limit: 12 }
    })
    items.value = res.items || []
  } catch {
    items.value = []
  } finally {
    loading.value = false
  }
}

watch(q, () => {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(loadResults, 220)
})

function setType(t: 'release' | 'track' | 'artist') {
  activeType.value = t
  loadResults()
}

function onOpen() {
  q.value = ''
  items.value = []
  loadResults()
  nextTick(() => searchInput.value?.focus())
}

function onPick(item: ShareItem) {
  emit('select', item)
  visible.value = false
}

function statusPillClass(s: string): string {
  switch (s) {
    case 'LIVE': return 'bg-emerald-400/15 text-emerald-300 border-emerald-400/25'
    case 'DELIVERED':
    case 'DELIVERED_TO_FUGA': return 'bg-blue-400/15 text-blue-300 border-blue-400/25'
    case 'TAKEDOWN': return 'bg-rose-400/15 text-rose-300 border-rose-400/25'
    case 'PENDING': return 'bg-orange-400/15 text-orange-300 border-orange-400/25'
    case 'DRAFT': return 'bg-amber-400/15 text-amber-300 border-amber-400/25'
    default: return 'bg-white/5 text-text-muted border-white/10'
  }
}
</script>
